import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Landmark, Tag } from "lucide-react";

const PROJECTS = [
  { name: "Ayat Boulevard", area: "Bole, Addis Ababa", price: "From ETB 8.5M", tags: ["Off-plan", "Mortgage eligible"], img: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&w=640&q=70" },
  { name: "CMC Heights", area: "CMC, Addis Ababa", price: "From ETB 12M", tags: ["Early pricing", "Mortgage eligible"], img: "https://images.unsplash.com/photo-1460317442991-0ec209397118?crop=entropy&cs=srgb&fm=jpg&w=640&q=70" },
  { name: "Skyline Residence", area: "Kazanchis, Addis Ababa", price: "From ETB 15M", tags: ["Off-plan", "Early pricing"], img: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?crop=entropy&cs=srgb&fm=jpg&w=640&q=70" },
  { name: "Green Valley", area: "Gulele, Addis Ababa", price: "From ETB 6.9M", tags: ["Mortgage eligible", "Early pricing"], img: "https://images.unsplash.com/photo-1448630360428-65456885c650?crop=entropy&cs=srgb&fm=jpg&w=640&q=70" },
];

const TagChip = ({ label }) => {
  const Icon = label === "Mortgage eligible" ? Landmark : Tag;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#1E3A8A]/8 px-2 py-0.5 text-[10px] font-semibold text-[#1E3A8A] dark:bg-[#7AA0FF]/12 dark:text-[#7AA0FF]">
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
};

export const NewProjects = () => {
  const navigate = useNavigate();
  return (
    <section className="bg-muted/40 py-12 sm:py-16" data-testid="new-projects-section">
      <div className="abay-container">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">New Projects</h2>
            <p className="mt-2 text-sm text-muted-foreground sm:text-base">Off-plan developments with early pricing and mortgage-eligible units.</p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/new-projects")}
            data-testid="new-projects-view-all"
            className="flex shrink-0 items-center gap-1 text-sm font-semibold text-[#1E3A8A] transition-colors duration-200 hover:text-[#172D6B] dark:text-[#7AA0FF]"
          >
            View all <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-6 grid grid-flow-col auto-cols-[80%] gap-4 overflow-x-auto pb-2 thin-scroll snap-x snap-mandatory sm:auto-cols-[46%] lg:grid-flow-row lg:grid-cols-4 lg:overflow-visible">
          {PROJECTS.map((p, i) => (
            <motion.button
              key={p.name}
              type="button"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              onClick={() => navigate("/new-projects")}
              data-testid={`new-project-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="tap-press group snap-start overflow-hidden rounded-2xl border border-border bg-card text-left shadow-[var(--shadow-sm)] transition-shadow duration-200 hover:shadow-[var(--shadow-md)]"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={p.img} alt={p.name} loading="lazy" decoding="async"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                <span className="absolute left-3 top-3 rounded-full bg-[#10B981] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-[var(--shadow-sm)]">
                  New
                </span>
              </div>
              <div className="p-4">
                <p className="text-sm font-bold text-foreground">{p.name}</p>
                <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" /> {p.area}
                </p>
                <p className="mt-2 text-sm font-extrabold text-[#1E3A8A] dark:text-[#7AA0FF]">{p.price}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {p.tags.map((t) => <TagChip key={t} label={t} />)}
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
};
